import CmsButton from "@/components/CmsButton";
import { useState, useEffect, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import smartlyLogo from "@/assets/smartly-logo.png";
import SpaceCanvas from "@/components/SpaceCanvas";
import { fallbackHeroVariant, useActiveHeroVariant } from "@/hooks/useHeroVariants";

interface HeroV2Props {
  onConsultationClick?: () => void;
}

const HeroV2 = ({ onConsultationClick }: HeroV2Props) => {
  const { data, isFetched } = useActiveHeroVariant();
  const hero = data ?? fallbackHeroVariant;
  const words = useMemo(
    () => hero.typewriter_words.split(",").map((word) => word.trim()).filter(Boolean),
    [hero.typewriter_words],
  );
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIndex] ?? "";

    if (!isDeleting && charIndex < current.length) {
      const timeout = setTimeout(() => setCharIndex((c) => c + 1), 80);
      return () => clearTimeout(timeout);
    }

    if (!isDeleting && charIndex === current.length) {
      const timeout = setTimeout(() => setIsDeleting(true), 1500);
      return () => clearTimeout(timeout);
    }

    if (isDeleting && charIndex > 0) {
      const timeout = setTimeout(() => setCharIndex((c) => c - 1), 50);
      return () => clearTimeout(timeout);
    }

    if (isDeleting && charIndex === 0) {
      const timeout = setTimeout(() => {
        setIsDeleting(false);
        setWordIndex((i) => (i + 1) % Math.max(words.length, 1));
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [charIndex, isDeleting, wordIndex, words]);

  useEffect(() => {
    setWordIndex(0);
    setCharIndex(0);
    setIsDeleting(false);
  }, [hero.id]);

  const renderHighlightedLine = () => {
    const line = hero.headline_secondary;
    const marker = hero.highlighted_phrase.trim();
    const markerIndex = marker ? line.indexOf(marker) : -1;
    if (markerIndex === -1) return line;
    const before = line.slice(0, markerIndex);
    const after = line.slice(markerIndex + marker.length);
    return (
      <>
        {before}
        <span className="relative inline-block">
          {marker}
          <svg className="absolute -bottom-1 left-[-4%] h-4 w-[108%] pointer-events-none" viewBox="0 0 300 16" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" aria-hidden="true">
            <path d="M4 11 Q 150 2 296 9" stroke="hsl(var(--primary))" strokeWidth="5" strokeLinecap="round" fill="none" className="animate-draw-line" />
            <path d="M10 11.5 Q 150 4 290 9.5" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round" fill="none" strokeOpacity="0.4" className="animate-draw-line" style={{ animationDelay: "0.15s" }} />
          </svg>
        </span>
        {after}
      </>
    );
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden starfield pt-16">
      <SpaceCanvas />

      {/* Nebula glows */}
      <div className="nebula-glow z-0 w-[500px] h-[500px] top-[20%] left-[10%] absolute bg-[hsl(0_100%_20%)]" />
      <div className="nebula-glow z-0 w-[400px] h-[400px] bottom-[10%] right-[5%] absolute bg-[hsl(280_60%_15%)]" style={{ animationDelay: '4s' }} />
      <div className="nebula-glow z-0 w-[350px] h-[350px] top-[60%] left-[50%] absolute bg-[hsl(220_80%_12%)]" style={{ animationDelay: '8s' }} />

      {/* Spiral galaxies */}
      <div className="space-galaxy galaxy-1" style={{ animationDuration: '149s', animationDelay: '-20s' }} />
      <div className="space-galaxy galaxy-2" style={{ animationDuration: '167s', animationDelay: '-50s' }} />

      {/* Star clusters */}
      <div className="star-cluster cluster-1" style={{ animationDuration: '97s', animationDelay: '-10s' }} />
      <div className="star-cluster cluster-2" style={{ animationDuration: '113s', animationDelay: '-45s' }} />

      {/* Radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] pointer-events-none z-[1]" />

      <div className={`container mx-auto px-4 text-center relative z-10 ${hero.show_branding ? "pt-20" : "pt-8"}`} style={{ visibility: isFetched ? "visible" : "hidden" }}>
        {hero.show_branding && (
          <>
            <img src={smartlyLogo} alt="Smartly Technologies logo" loading="eager" fetchPriority="high" className="h-12 sm:h-16 md:h-20 mx-auto mb-4" />
            <p className="text-primary font-display text-sm uppercase tracking-[0.3em] mb-6">
              Smartly Technologies
            </p>
          </>
        )}

        <div className="relative">
          <div className="absolute inset-0 mx-auto w-[80%] h-[120%] bg-red-600/20 blur-[80px] rounded-full pointer-events-none -z-[1]" />
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-2" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.7), 0 2px 8px rgba(0,0,0,0.5)' }}>
            {hero.headline}
          </h1>
          <p className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.7), 0 2px 8px rgba(0,0,0,0.5)' }}>
            {renderHighlightedLine()}
          </p>
        </div>

        {/* Typewriter animation */}
        <div className="h-12 md:h-16 flex items-center justify-center mb-5 sm:mb-8">
          <span className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-muted-foreground">
            <span className="text-primary font-bold">{hero.typewriter_prefix}</span>{" "}
            <span className="text-foreground font-semibold">{(words[wordIndex] ?? "").substring(0, charIndex)}</span>
            <span className="typing-cursor text-primary">|</span>
          </span>
        </div>

        <p className="text-foreground text-base md:text-lg max-w-xl mx-auto mb-6 sm:mb-10 bg-black/10 backdrop-blur-[1px] rounded-md px-4 py-3 inline-block">
          {hero.subheadline}
        </p>

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 justify-center max-w-4xl mx-auto">
          {hero.cta_primary_label && <CmsButton size="lg" className="h-10 sm:h-11 glow-red" href={hero.cta_primary_link} onFallback={onConsultationClick}>
            {hero.cta_primary_label} <ArrowRight className="ml-2 h-4 w-4" />
          </CmsButton>}
          {hero.cta_secondary_label && <CmsButton size="lg" variant="secondary" className="h-10 sm:h-11" href={hero.cta_secondary_link} onFallback={onConsultationClick}>
            {hero.cta_secondary_label}
          </CmsButton>}
          {hero.cta_tertiary_label && <CmsButton size="lg" variant="outline" className="h-10 sm:h-11" href={hero.cta_tertiary_link} onFallback={onConsultationClick}>
            {hero.cta_tertiary_label}
          </CmsButton>}
        </div>
      </div>

    </section>
  );
};

export default HeroV2;
